import { useState, useEffect, useRef, useCallback } from "react";
import { X, ChevronRight, ChevronLeft, Sparkles } from "lucide-react";
import { apiClient } from "../utils/api/client";
import type { TelegramUser } from "../types";

interface OnboardingStep {
  id: string;
  target: string; // CSS selector или data-onboarding-id
  title: string;
  content: string;
  position?: "top" | "bottom" | "left" | "right" | "center";
  offset?: { x?: number; y?: number };
}

interface OnboardingTourProps {
  steps: OnboardingStep[];
  storageKey: string; // Например, "onboarding-sandbox-completed" -> page_key будет "sandbox"
  user?: TelegramUser | null; // Пользователь для проверки авторизации
  onComplete?: () => void;
  onStartRef?: React.MutableRefObject<(() => void) | null>;
}

export const OnboardingTour = ({ steps, storageKey, user, onComplete, onStartRef }: OnboardingTourProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [windowSize, setWindowSize] = useState({ 
    width: typeof window !== 'undefined' ? window.innerWidth : 1024, 
    height: typeof window !== 'undefined' ? window.innerHeight : 768 
  });
  const overlayRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Извлекаем page_key из storageKey (например, "onboarding-sandbox-completed" -> "sandbox")
  const getPageKey = useCallback(() => {
    // Убираем префикс "onboarding-" и суффикс "-completed"
    const key = storageKey.replace(/^onboarding-/, '').replace(/-completed$/, '');
    return key || storageKey; // Если не удалось извлечь, используем весь ключ
  }, [storageKey]);

  const completeOnboarding = useCallback(async () => {
    const pageKey = getPageKey();
    
    if (user?.id) {
      // Авторизованный пользователь — сохраняем в БД; localStorage дублируем для единообразия с анонимами и офлайн-фолбэком
      try {
        await apiClient.post(`/user/${user.id}/onboarding/complete`, {
          page_key: pageKey,
        }, {
          invalidateCache: [`GET:/user/${user.id}/onboarding/status`],
        });
        localStorage.setItem(storageKey, "true");
      } catch (error) {
        console.error("Failed to save onboarding status to server:", error);
        localStorage.setItem(storageKey, "true");
      }
    } else {
      localStorage.setItem(storageKey, "true");
    }
    
    setIsVisible(false);
    onComplete?.();
  }, [storageKey, user, onComplete, getPageKey]);

  const updatePosition = useCallback(() => {
    if (currentStep >= steps.length) return;

    const step = steps[currentStep];
    const element = document.querySelector(step.target);
    const isMobile = windowSize.width < 768;

    if (!element) {
      // Элемент не найден (например, панель свёрнута) — показываем тултип по центру, не оставляем только тёмный экран
      const w = isMobile ? Math.min(344, windowSize.width - 40) : 384;
      const h = 200;
      setPosition({
        top: Math.max(20, (windowSize.height - h) / 2),
        left: Math.max(20, (windowSize.width - w) / 2),
      });
      return;
    }

    const rect = element.getBoundingClientRect();
    const stepPosition = step.position || "bottom";
    const offset = step.offset || { x: 0, y: 0 };

    // Получаем реальные размеры тултипа
    let tooltipWidth = isMobile ? Math.min(344, windowSize.width - 40) : 384; // max-w-sm = 384px (fallback)
    let tooltipHeight = 200; // примерная высота (fallback)
    if (tooltipRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      tooltipWidth = tooltipRect.width > 0 ? tooltipRect.width : (isMobile ? Math.min(344, windowSize.width - 40) : 384);
      tooltipHeight = tooltipRect.height > 0 ? tooltipRect.height : 200;
    }

    const padding = isMobile ? 20 : 20; // отступ от краев экрана
    const gap = isMobile ? 12 : 16; // отступ от элемента

    let top = 0;
    let left = 0;

    // Функция проверки пересечения попапа с элементом
    const checkOverlap = (tooltipTop: number, tooltipLeft: number) => {
      const tooltipRight = tooltipLeft + tooltipWidth;
      const tooltipBottom = tooltipTop + tooltipHeight;
      const elementRight = rect.right;
      const elementBottom = rect.bottom;
      
      // Проверяем пересечение по горизонтали и вертикали
      const horizontalOverlap = !(tooltipRight < rect.left || tooltipLeft > elementRight);
      const verticalOverlap = !(tooltipBottom < rect.top || tooltipTop > elementBottom);
      
      return horizontalOverlap && verticalOverlap;
    };

    // Вычисляем позицию в зависимости от предпочтительного направления
    switch (stepPosition) {
      case "top":
        top = rect.top - tooltipHeight - gap + offset.y!;
        left = rect.left + offset.x!;
        // Если не помещается сверху или перекрывает элемент, размещаем снизу
        if (top < padding || checkOverlap(top, left)) {
          top = rect.bottom + gap + offset.y!;
          // Проверяем, что снизу помещается и не перекрывает
          if (top + tooltipHeight > window.innerHeight - padding || checkOverlap(top, left)) {
            // Размещаем справа от элемента, если возможно
            left = rect.right + gap + offset.x!;
            top = rect.top + offset.y!;
            if (left + tooltipWidth > windowSize.width - padding) {
              // Размещаем слева от элемента
              left = rect.left - tooltipWidth - gap + offset.x!;
              if (left < padding) {
                // Последний вариант - размещаем в доступном месте, но не перекрывая элемент
                left = Math.max(padding, Math.min(windowSize.width - tooltipWidth - padding, rect.left + rect.width / 2 - tooltipWidth / 2));
                top = rect.bottom + gap + offset.y!;
                if (top + tooltipHeight > windowSize.height - padding) {
                  top = Math.max(padding, windowSize.height - tooltipHeight - padding);
                }
              }
            }
          }
        }
        break;
      case "bottom":
        // Всегда стараемся разместить снизу
        top = rect.bottom + gap + offset.y!;
        left = rect.left + offset.x!;
        // Проверяем, что снизу помещается и не перекрывает элемент
        if (top + tooltipHeight > windowSize.height - padding || checkOverlap(top, left)) {
          // Не помещается снизу, размещаем справа от элемента
          left = rect.right + gap + offset.x!;
          top = rect.top + offset.y!;
          if (left + tooltipWidth > windowSize.width - padding || checkOverlap(top, left)) {
            // Не помещается справа, размещаем слева от элемента
            left = rect.left - tooltipWidth - gap + offset.x!;
            if (left < padding || checkOverlap(top, left)) {
              // Не помещается и слева, размещаем выше элемента
              top = rect.top - tooltipHeight - gap + offset.y!;
              left = rect.left + offset.x!;
              if (top < padding || checkOverlap(top, left)) {
                // Последний вариант - размещаем в доступном месте, но не перекрывая элемент
                left = Math.max(padding, Math.min(windowSize.width - tooltipWidth - padding, rect.left + rect.width / 2 - tooltipWidth / 2));
                top = rect.bottom + gap + offset.y!;
                if (top + tooltipHeight > windowSize.height - padding) {
                  top = Math.max(padding, windowSize.height - tooltipHeight - padding);
                }
              }
            }
          }
        }
        break;
      case "left":
        top = rect.top + rect.height / 2 + offset.y!;
        left = rect.left - tooltipWidth - gap + offset.x!;
        // Центрируем по вертикали
        top -= tooltipHeight / 2;
        // Если не помещается слева или перекрывает элемент, размещаем справа
        if (left < padding || checkOverlap(top, left)) {
          left = rect.right + gap + offset.x!;
          // Проверяем, что справа помещается
          if (left + tooltipWidth > windowSize.width - padding || checkOverlap(top, left)) {
            // На мобильных размещаем сверху или снизу
            if (isMobile) {
              // Пробуем снизу
              top = rect.bottom + gap + offset.y!;
              left = Math.max(padding, Math.min(windowSize.width - tooltipWidth - padding, rect.left + rect.width / 2 - tooltipWidth / 2));
              if (top + tooltipHeight > windowSize.height - padding) {
                // Если не помещается снизу, размещаем сверху
                top = rect.top - tooltipHeight - gap + offset.y!;
                if (top < padding) {
                  top = padding;
                }
              }
            } else {
              // Размещаем в доступном месте
              left = Math.max(padding, Math.min(windowSize.width - tooltipWidth - padding, rect.left + rect.width / 2 - tooltipWidth / 2));
              top = Math.max(padding, Math.min(windowSize.height - tooltipHeight - padding, rect.top + rect.height / 2 - tooltipHeight / 2));
            }
          }
        }
        break;
      case "right":
        top = rect.top + rect.height / 2 + offset.y!;
        left = rect.right + gap + offset.x!;
        // Центрируем по вертикали
        top -= tooltipHeight / 2;
        // Проверяем, что справа помещается и не перекрывает элемент
        if (left + tooltipWidth > windowSize.width - padding || checkOverlap(top, left)) {
          // Не помещается справа, пробуем слева
          left = rect.left - tooltipWidth - gap + offset.x!;
          if (left < padding || checkOverlap(top, left)) {
            // Не помещается и слева, размещаем в доступном месте
            left = Math.max(padding, Math.min(windowSize.width - tooltipWidth - padding, rect.left + rect.width / 2 - tooltipWidth / 2));
            top = Math.max(padding, Math.min(windowSize.height - tooltipHeight - padding, rect.top + rect.height / 2 - tooltipHeight / 2));
          }
        }
        break;
      case "center":
        top = windowSize.height / 2 + offset.y!;
        left = windowSize.width / 2 + offset.x!;
        // Смещаем влево на половину ширины попапа
        left -= tooltipWidth / 2;
        // Проверяем, что не перекрывает элемент
        if (checkOverlap(top, left)) {
          // Размещаем справа от элемента
          left = rect.right + gap + offset.x!;
          top = rect.top + offset.y!;
          if (left + tooltipWidth > windowSize.width - padding) {
            // Размещаем слева от элемента
            left = rect.left - tooltipWidth - gap + offset.x!;
            if (left < padding) {
              left = Math.max(padding, Math.min(windowSize.width - tooltipWidth - padding, rect.left + rect.width / 2 - tooltipWidth / 2));
            }
          }
        }
        break;
    }

    // Финальная проверка и корректировка границ экрана
    if (left < padding) {
      left = padding;
    } else if (left + tooltipWidth > windowSize.width - padding) {
      left = windowSize.width - tooltipWidth - padding;
    }

    if (top < padding) {
      top = padding;
    } else if (top + tooltipHeight > windowSize.height - padding) {
      // На мобильных устройствах ограничиваем высоту и делаем прокручиваемым
      if (isMobile) {
        const maxHeight = windowSize.height - top - padding;
        tooltipHeight = Math.min(tooltipHeight, maxHeight);
      } else {
        top = windowSize.height - tooltipHeight - padding;
      }
    }

    // Финальная проверка на перекрытие элемента после всех корректировок
    if (checkOverlap(top, left)) {
      // Если все еще перекрывает, размещаем справа от элемента
      left = rect.right + gap + offset.x!;
      top = rect.top + offset.y!;
      // Если не помещается справа, размещаем слева
      if (left + tooltipWidth > windowSize.width - padding) {
        left = rect.left - tooltipWidth - gap + offset.x!;
        if (left < padding) {
          // Последний вариант - размещаем ниже элемента, но не перекрывая
          left = rect.left + offset.x!;
          top = rect.bottom + gap + offset.y!;
          if (top + tooltipHeight > windowSize.height - padding) {
            top = Math.max(padding, windowSize.height - tooltipHeight - padding);
          }
        }
      }
    }

    setPosition({ top, left });
  }, [currentStep, steps, completeOnboarding, windowSize]);

  const startOnboarding = useCallback(() => {
    setCurrentStep(0);
    setIsVisible(true);
    setTimeout(() => {
      updatePosition();
    }, 300);
  }, [updatePosition]);

  // Экспортируем функцию запуска через ref
  useEffect(() => {
    if (onStartRef) {
      onStartRef.current = startOnboarding;
      return () => {
        if (onStartRef) {
          onStartRef.current = null;
        }
      };
    }
  }, [onStartRef, startOnboarding]);

  useEffect(() => {
    // Проверяем, был ли онбординг уже показан
    const checkOnboardingStatus = async () => {
      if (user?.id) {
        // Авторизованный пользователь - проверяем в БД
        try {
          const status = await apiClient.get<{ completed_pages: string[] }>(
            `/user/${user.id}/onboarding/status`
          );
          const pageKey = getPageKey();
          const hasSeenOnboarding = status.completed_pages.includes(pageKey);
          
          if (!hasSeenOnboarding && steps.length > 0) {
            setIsVisible(true);
            setTimeout(() => {
              updatePosition();
            }, 100);
          }
        } catch (error) {
          // 404 и прочие ошибки — считаем онбординг не пройденным, не логируем как ошибку
          const isNotFound =
            error instanceof Error && error.message.includes("Not Found");
          if (!isNotFound) {
            console.error("Failed to check onboarding status from server:", error);
          }
          // Fallback на localStorage при ошибке
          const hasSeenOnboarding = localStorage.getItem(storageKey) === "true";
          if (!hasSeenOnboarding && steps.length > 0) {
            setIsVisible(true);
            setTimeout(() => {
              updatePosition();
            }, 100);
          }
        }
      } else {
        // Неавторизованный пользователь - проверяем localStorage
        const hasSeenOnboarding = localStorage.getItem(storageKey) === "true";
        if (!hasSeenOnboarding && steps.length > 0) {
          setIsVisible(true);
          setTimeout(() => {
            updatePosition();
          }, 100);
        }
      }
    };
    
    checkOnboardingStatus();
  }, [storageKey, steps.length, updatePosition, user, getPageKey]);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
      updatePosition();
    };

    if (isVisible && currentStep < steps.length) {
      // Обновляем позицию после рендера тултипа для получения реальных размеров
      // Используем несколько попыток для получения актуальных размеров
      const updateWithDelay = () => {
        // Первая попытка сразу
        updatePosition();
        
        // Вторая попытка после requestAnimationFrame
        requestAnimationFrame(() => {
          updatePosition();
          
          // Третья попытка после небольшой задержки для гарантии
          setTimeout(() => {
            updatePosition();
          }, 100);
        });
      };
      
      // Небольшая задержка перед первым обновлением
      const timer = setTimeout(updateWithDelay, 50);
      
      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", updatePosition, true);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("scroll", updatePosition, true);
      };
    }
  }, [isVisible, currentStep, steps, updatePosition]);

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeOnboarding();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const skipOnboarding = () => {
    completeOnboarding();
  };

  if (!isVisible || currentStep >= steps.length) {
    return null;
  }

  const step = steps[currentStep];
  const element = document.querySelector(step.target);

  return (
    <>
      {/* Overlay с вырезом для целевого элемента */}
      {element && (() => {
        const rect = element.getBoundingClientRect();
        const borderRadius = 12;
        const padding = 4;
        const holeTop = rect.top - padding;
        const holeLeft = rect.left - padding;
        const holeWidth = rect.width + padding * 2;
        const holeHeight = rect.height + padding * 2;
        const maskId = `onboarding-hole-${currentStep}`;
        
        return (
          <>
            <div ref={overlayRef} className="fixed inset-0 z-[9998]" onClick={skipOnboarding}>
              <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
                <defs>
                  <mask id={maskId}>
                    <rect width="100%" height="100%" fill="white" />
                    <rect
                      x={holeLeft}
                      y={holeTop}
                      width={holeWidth}
                      height={holeHeight}
                      rx={borderRadius}
                      ry={borderRadius}
                      fill="black"
                    />
                  </mask>
                </defs>
                <rect width="100%" height="100%" fill="rgba(0,0,0,0.5)" mask={`url(#${maskId})`} />
              </svg>
            </div>
            {/* Подсветка элемента */}
            <div
              className="fixed border-2 border-primary rounded-xl shadow-lg pointer-events-none z-[9998]"
              style={{
                top: holeTop,
                left: holeLeft,
                width: holeWidth,
                height: holeHeight,
                borderRadius: `${borderRadius}px`,
                boxShadow: "0 0 20px rgba(10, 191, 83, 0.5)",
                backgroundColor: 'transparent',
              }}
            />
          </>
        );
      })()}
      {!element && (
        <div
          ref={overlayRef}
          className="fixed inset-0 z-[9998] bg-black/50 transition-opacity"
          onClick={skipOnboarding}
        />
      )}

      {/* Тултип с информацией */}
      <div
        ref={tooltipRef}
        className="fixed z-[9999] bg-white rounded-xl shadow-2xl p-4 sm:p-6 max-w-sm pointer-events-auto"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          maxWidth: windowSize.width < 768 ? `${Math.min(344, windowSize.width - 40)}px` : '384px',
          maxHeight: windowSize.width < 768 ? `${Math.min(500, Math.max(200, windowSize.height - position.top - 20))}px` : 'none',
          overflowY: windowSize.width < 768 ? 'auto' : 'visible',
        }}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="bg-primary/10 rounded-lg p-2 flex-shrink-0">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-neutral-text mb-1">{step.title}</h3>
            <div className="text-sm text-neutral-text/70 leading-relaxed whitespace-pre-line">
              {step.content.split('\n').map((line, index) => {
                if (line.trim().startsWith('•')) {
                  return (
                    <div key={index} className="flex items-start gap-2 mb-1">
                      <span className="text-primary mt-0.5">•</span>
                      <span>{line.replace(/^•\s*/, '')}</span>
                    </div>
                  );
                }
                return <div key={index}>{line}</div>;
              })}
            </div>
          </div>
          <button
            onClick={skipOnboarding}
            className="p-1 rounded-lg hover:bg-neutral-light transition-colors flex-shrink-0"
            aria-label="Закрыть"
          >
            <X className="w-5 h-5 text-neutral-text/60" />
          </button>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-xs text-neutral-text/50">
            Шаг {currentStep + 1} из {steps.length}
          </div>
          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <button
                onClick={prevStep}
                className="px-3 py-1.5 text-sm border border-neutral-border rounded-lg hover:bg-neutral-light transition-colors flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                Назад
              </button>
            )}
            <button
              onClick={skipOnboarding}
              className="px-3 py-1.5 text-sm text-neutral-text/70 border border-neutral-border rounded-lg hover:bg-neutral-light transition-colors"
            >
              Завершить онбординг
            </button>
            <button
              onClick={nextStep}
              className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-1"
            >
              {currentStep < steps.length - 1 ? (
                <>
                  Далее
                  <ChevronRight className="w-4 h-4" />
                </>
              ) : (
                "Завершить"
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

